/**
 * WorkflowControls - Toolbar for workflow operations
 * Save, Export, Import, Validate, etc.
 */

import { memo, useRef, useState, useCallback } from 'react';
import { useWorkflowPersistence, useWorkflowValidation, useWorkflowOperations, useWorkflowPalette } from '../../../../stores/workflow';
import type { WorkflowDefinition } from '../../../../stores/workflow/types';
import { ConfirmDialog, useNotificationService } from '../../../Notification';
import { useAdminTranslation } from '../../../../modules/i18n';

interface WorkflowControlsProps {
  onSave?: (workflow: WorkflowDefinition) => Promise<void>;
}

export const WorkflowControls = memo(({ onSave }: WorkflowControlsProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useAdminTranslation();
  const { showError } = useNotificationService();

  const {
    currentWorkflowName,
    currentWorkflowDescription: _currentWorkflowDescription,
    isDirty,
    isSaving,
    setCurrentWorkflowName,
    setCurrentWorkflowDescription: _setCurrentWorkflowDescription,
    markAsSaved,
    setIsSaving,
    exportWorkflow
  } = useWorkflowPersistence();

  const { validationErrors, isValid, validateWorkflow } = useWorkflowValidation();
  const { loadWorkflow, clearWorkspace } = useWorkflowOperations();
  const { isPaletteOpen, togglePalette } = useWorkflowPalette();

  // Save workflow
  const handleSave = useCallback(async () => {
    if (!validateWorkflow()) {
      return;
    }

    setIsSaving(true);
    try {
      const workflow = exportWorkflow();
      if (onSave) {
        await onSave(workflow);
      }
      markAsSaved();
    } catch (error) {
      console.error('Failed to save workflow:', error);
    } finally {
      setIsSaving(false);
    }
  }, [validateWorkflow, setIsSaving, exportWorkflow, onSave, markAsSaved]);

  // Export workflow as JSON file
  const handleExport = useCallback(() => {
    const workflow = exportWorkflow();
    const blob = new Blob([JSON.stringify(workflow, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentWorkflowName.replace(/\s+/g, '_').toLowerCase()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [exportWorkflow, currentWorkflowName]);

  // Import workflow from JSON file
  const handleImport = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          const workflow: WorkflowDefinition = JSON.parse(content);
          loadWorkflow(workflow);
        } catch (error) {
          console.error('Failed to import workflow:', error);
          showError(t('workflow.import.failedTitle'), t('workflow.import.failedMessage'));
        }
      };
      reader.readAsText(file);

      // Reset file input
      if (event.target) {
        event.target.value = '';
      }
    },
    [loadWorkflow, showError, t]
  );

  // New workflow — dirty state triggers a ConfirmDialog; clean state clears immediately.
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);

  const handleNew = useCallback(() => {
    if (isDirty) {
      setShowUnsavedConfirm(true);
      return;
    }
    clearWorkspace();
  }, [isDirty, clearWorkspace]);

  const confirmNewWorkflow = useCallback(() => {
    setShowUnsavedConfirm(false);
    clearWorkspace();
  }, [clearWorkspace]);

  // Validate workflow
  const handleValidate = useCallback(() => {
    validateWorkflow();
  }, [validateWorkflow]);

  const errorCount = validationErrors.filter((e) => e.type === 'error').length;
  const warningCount = validationErrors.filter((e) => e.type === 'warning').length;

  return (
    <>
    <div className='flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-border-dark dark:bg-dark-background-secondary'>
      {/* Toggle Palette */}
      <button
        onClick={togglePalette}
        className={`rounded-lg p-2 transition-colors ${
          isPaletteOpen ? 'bg-primary/10 text-primary' : 'text-gray-500 hover:bg-gray-100 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
        }`}
        title={isPaletteOpen ? 'Hide Palette' : 'Show Palette'}
      >
        <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z'
          />
        </svg>
      </button>

      <div className='h-6 w-px bg-gray-200 dark:bg-dark-background-tertiary' />

      {/* Workflow Name */}
      <input
        type='text'
        value={currentWorkflowName}
        onChange={(e) => setCurrentWorkflowName(e.target.value)}
        className='w-48 rounded-lg border border-gray-200 bg-transparent px-3 py-1.5 text-sm font-medium text-gray-900 focus:border-primary focus:outline-none dark:border-border-dark dark:text-dark-text'
        placeholder='Workflow Name'
      />

      {/* Dirty indicator */}
      {isDirty && (
        <span className='text-xs text-amber-500' title='Unsaved changes'>
          *
        </span>
      )}

      <div className='h-6 w-px bg-gray-200 dark:bg-dark-background-tertiary' />

      {/* New */}
      <button
        onClick={handleNew}
        className='rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
        title='New Workflow'
      >
        <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
        </svg>
      </button>

      {/* Import */}
      <button
        onClick={handleImport}
        className='rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
        title='Import Workflow'
      >
        <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12' />
        </svg>
      </button>
      <input ref={fileInputRef} type='file' accept='.json' onChange={handleFileChange} className='hidden' />

      {/* Export */}
      <button
        onClick={handleExport}
        className='rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
        title='Export Workflow'
      >
        <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4' />
        </svg>
      </button>

      <div className='h-6 w-px bg-gray-200 dark:bg-dark-background-tertiary' />

      {/* Validate */}
      <button
        onClick={handleValidate}
        className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition-colors ${
          isValid
            ? 'text-gray-500 hover:bg-gray-100 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
            : 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400'
        }`}
        title='Validate Workflow'
      >
        <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
        </svg>
        {errorCount > 0 && <span>{errorCount}</span>}
        {warningCount > 0 && <span className='text-amber-500'>({warningCount})</span>}
      </button>

      {/* Save */}
      <button
        onClick={handleSave}
        disabled={isSaving || !isDirty}
        className={`flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${
          isDirty ? 'bg-primary text-white hover:bg-primary/90' : 'bg-gray-100 text-gray-400 dark:bg-dark-background-tertiary dark:text-dark-text-secondary'
        }`}
        title='Save Workflow'
      >
        {isSaving ? (
          <>
            <svg className='h-4 w-4 animate-spin' fill='none' viewBox='0 0 24 24'>
              <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
              />
            </svg>
            Saving...
          </>
        ) : (
          <>
            <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4'
              />
            </svg>
            Save
          </>
        )}
      </button>
    </div>
    <ConfirmDialog
      isOpen={showUnsavedConfirm}
      title={t('workflow.unsavedChanges.title')}
      message={t('workflow.unsavedChanges.message')}
      confirmText={t('workflow.unsavedChanges.confirmButton')}
      cancelText={t('workflow.unsavedChanges.cancelButton')}
      confirmVariant='danger'
      onConfirm={confirmNewWorkflow}
      onCancel={() => setShowUnsavedConfirm(false)}
    />
    </>
  );
});

WorkflowControls.displayName = 'WorkflowControls';
